import { call, put, StrictEffect, takeLatest } from "redux-saga/effects";

import { Paginated } from "@/jenesys-types/data/common";
import { DataType } from "@/jenesys-types/data/enums";
import { extractError } from "@/jenesys-types/errors";
import { Client, Company } from "@/jenesys-types/jenesys-entities/config";

import { create, performDataAction } from "@/services/api";
import { toastError, toastSuccess } from "@/util/helpers";

import {
  clientsFailure,
  clientsSuccess,
  companyFailure,
  companySuccess,
  createClient,
  createClientFailure,
  createClientSuccess,
  loadClients,
  LoadClientsAction,
  setCompany,
  SetCompanyAction,
  updateCompany,
  UpdateCompanyAction,
  updateCompanyFailure,
  updateCompanySuccess
} from "./companies";

export function* loadClientsSaga(
  action: LoadClientsAction
): Generator<StrictEffect, void, any> {
  const { companyId, search } = action.payload;
  const limit = 300;
  const offset = 0;
  try {
    const companyClientsResponse: Paginated<Client> = yield call(
      performDataAction,
      DataType.Clients,
      "getClientsWithParams",
      {
        companyId,
        search,
        limit,
        offset
      }
    );

    yield put(clientsSuccess(companyClientsResponse));
  } catch (error) {
    yield put(clientsFailure(extractError(error)));
  }
}

export function* setCompanySaga(
  action: SetCompanyAction
): Generator<StrictEffect, void, any> {
  const { companyId } = action.payload;
  try {
    const companyResponse: Company = yield call(
      performDataAction,
      DataType.Company,
      "list",
      {
        companyId
      }
    );
    yield put(companySuccess(companyResponse));
  } catch (error) {
    yield put(companyFailure(extractError(error)));
  }
}

export function* updateCompanySaga(
  action: UpdateCompanyAction
): Generator<StrictEffect, void, any> {
  const { companyId, data, onSuccess } = action.payload;
  try {
    const companyResponse: Company = yield call(
      performDataAction,
      DataType.Update,
      "update",
      {
        id: companyId
      },
      undefined,
      data
    );
    yield put(updateCompanySuccess(companyResponse, onSuccess));
  } catch (error) {
    yield put(updateCompanyFailure(extractError(error)));
  }
}

export function* createClientSaga(
  action: ReturnType<typeof createClient>
): Generator<StrictEffect, void, any> {
  const {
    clientData,
    companyId,
    onSuccess: closeClientCreationModal
  } = action.payload;

  try {
    const client: Client = yield call(create, DataType.Clients, clientData, {
      companyId
    });

    yield put(createClientSuccess(client));
    toastSuccess("Client created successfully");

    if (closeClientCreationModal) {
      closeClientCreationModal(client);
    }
  } catch (error) {
    yield put(createClientFailure(extractError(error)));
    toastError("Failed to create client", error);
  }
}

export default function* CompanysSaga() {
  yield takeLatest(loadClients.type, loadClientsSaga);
  yield takeLatest(setCompany.type, setCompanySaga);
  yield takeLatest(updateCompany.type, updateCompanySaga);
  yield takeLatest(createClient.type, createClientSaga);
}
